/* eslint-disable @atlaskit/ui-styling-standard/no-unsafe-values */
/* eslint-disable @atlaskit/ui-styling-standard/no-imported-style-values */
/* eslint-disable @atlaskit/design-system/ensure-design-token-usage */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useCallback } from 'react';

import { css, cssMap, jsx } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRouter } from 'react-resource-router';

import IconButton from '@atlaskit/button/icon/button';
import BugIcon from '@atlaskit/icon/core/bug';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import UFOSegment from '@atlaskit/react-ufo/ufo-segment';
import Spinner from '@atlaskit/spinner/spinner';
import { token } from '@atlaskit/tokens';

import { useAnalyticsEvents } from '../../../common/analytics/generated/use-analytics-events';
import { usePanelContext } from '../../../hooks/use-panel-context';
import { useAtlassianUserContext } from '../../../hooks/use-user-context';
import { CLI_ROUTE_NAME } from '../../../routes';
import { SiteSwitcher } from '../SiteSwitcher';
import { BrandHomeLink } from './BrandHomeLink';
import type { NAV_BAR_HEIGHT_PX as NAV_BAR_HEIGHT_PX_TYPE } from './constants';
import { FeedbackControl } from './FeedbackControl';
import { messages } from './messages';
import { NavButtons } from './NavButtons';
import { NavMenu } from './NavMenu';
import { UserAvatarDropdown } from './UserAvatarDropdown';
const NAV_BAR_HEIGHT_PX_STATIC = 88 satisfies typeof NAV_BAR_HEIGHT_PX_TYPE;

export { NAV_BAR_HEIGHT_PX } from './constants';

const styles = cssMap({
	navBar: {
		width: '100%',
		backgroundColor: '#111213',
		position: 'fixed',
		insetInlineStart: 0,
		top: 0,
		zIndex: 10,
	},
	navBarContent: {
		boxSizing: 'border-box',
		width: '100%',
		maxWidth: '1264px',
		height: `${NAV_BAR_HEIGHT_PX_STATIC}px`,
		display: 'flex',
		alignItems: 'center',
		paddingInline: token('space.400'),
		marginInline: 'auto',
		// Phones have to fit the nav buttons alongside the logo and the right-hand
		// controls, which the bar's usual 32px gutter leaves no room for.
		'@media (max-width: 30rem)': {
			paddingInline: token('space.200'),
		},
	},
	rightControls: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		gap: token('space.200'),
		justifySelf: 'end',
		// Passes the shrink down to the site switcher's label, the only control
		// here without a bounded width, so a long site name truncates rather than
		// carrying these controls off the edge of a narrow bar.
		minWidth: 0,
	},
	loginButton: {
		display: 'inline-flex',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		paddingInline: '10.5px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		paddingBlock: '6.5px',
		alignItems: 'center',
		gap: token('space.075'),
		borderRadius: token('radius.medium'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-shape
		borderWidth: '1.5px',
		borderStyle: 'solid',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		borderColor: '#505258',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		backgroundColor: '#111213',
		cursor: 'pointer',
		transition: token('motion.button.hovered'),
		zIndex: 1,
		font: token('font.body'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '20px',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#B7B9BE',
		fontWeight: token('font.weight.medium'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	loginButtonMotion: {
		transition: token('motion.button.hovered'),
	},
});

const navBarContentStyles = css({
	width: '100%',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
});

const leftNavStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.400'),
	'@media (max-width: 30rem)': {
		gap: token('space.100'),
	},
});

export const NavigationBar = (): JSX.Element => {
	const { identity: user, loading, error, isAuthed, attemptUserLogin } = useAtlassianUserContext();
	const { formatMessage } = useIntl();
	const { togglePanel, isOpen } = usePanelContext();
	const { fireEvent } = useAnalyticsEvents();
	const [{ route }] = useRouter();
	// `/cli` is a dedicated install experience: the site switcher and the nav
	// login button do not apply there. The nav buttons do still render, so
	// visitors can move between the two pages.
	const isCliPage = route?.name === CLI_ROUTE_NAME;

	const handleLoginClick = useCallback((): void => {
		fireEvent('ui.button.clicked.twgMicrositeLogin', { location: 'navBar' });
		attemptUserLogin();
	}, [fireEvent, attemptUserLogin]);

	const brandLabel = formatMessage(messages.teamworkGraphBrand);

	return (
		<UFOSegment name="twg-microsite.nav-bar">
			<div css={styles.navBar}>
				<div css={styles.navBarContent}>
					<div css={navBarContentStyles}>
						<div css={leftNavStyles}>
							<BrandHomeLink />
							{!loading && <NavButtons ariaLabel={brandLabel} isAuthed={isAuthed} />}
						</div>

						<div css={styles.rightControls}>
							{loading && (
								// `data-vc-ignore-if-no-layout-shift="true"` excludes the
								// auth-loading spinner from React UFO's VC tracking. The
								// `@atlaskit/spinner` SVG runs an infinite CSS animation in
								// a fixed-size box (no layout shift) so VC can safely
								// ignore its mutations — otherwise the spinner kicks the
								// VC quiet window for the full duration of auth resolution
								// and prevents the page-load interaction from sealing.
								<span data-vc-ignore-if-no-layout-shift="true">
									<Spinner size="small" interactionName="twg-microsite.nav-bar.user-loading" />
								</span>
							)}

							{!loading && <FeedbackControl />}

							{error && !isCliPage && (
								<button
									css={[
										styles.loginButton,
										fg('platform-dst-motion-uplift-custom-button') && styles.loginButtonMotion,
									]}
									onClick={handleLoginClick}
									aria-label={formatMessage(messages.loginAriaLabel)}
								>
									<FormattedMessage {...messages.loginButton} />
								</button>
							)}

							{!loading && !error && user && !isCliPage && <SiteSwitcher />}
							{!loading && !error && user && <UserAvatarDropdown user={user} />}
							{fg('twg_microsite_debug_panel') && (
								<IconButton
									icon={BugIcon}
									label="Debug Menu Toggle"
									appearance={isOpen ? 'discovery' : 'default'}
									onClick={togglePanel}
								/>
							)}
							{/*
							 * Last in the cluster, so the hamburger is the outermost control
							 * on the bar. Unlike its neighbours it does not wait on `loading`:
							 * nothing in its popout comes from the user context, so there is no
							 * reason to withhold navigation while auth resolves.
							 */}
							<NavMenu />
						</div>
					</div>
				</div>
			</div>
		</UFOSegment>
	);
};
